import {Injectable} from '@angular/core';
import {PaymentTransactionService} from "./payment-transaction.service";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {Observable} from "rxjs";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {UserCardResponse} from "../interface/userCardResponse";
import {PaymentTransactionRequest} from "../models/paymentTransactionRequest";
import {PaymentTransactionResponse} from "../interface/paymentTransactionResponse";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {DUPLICATE_MONEY_LUX_UUID_MESSAGE, PAYMENT_FAILURE_MESSAGE} from "../constants/global.constants";
import {PaymentMethod} from "../enums/paymentMethod";
import {WITH_COMMISSION, WITHOUT_COMMISSION} from "../constants/payment.constant";
import CryptoJS from 'crypto-js';
import {EasywalletResponse} from "../interface/easywalletResponse";
import {PaymentTransactionUtil} from "../utilityClass/paymentTransactionUtil";

@Injectable({
  providedIn: "root"
})
export class EasywalletService {

  private readonly easywalletlUrl = 'https://merchantapi.easypay.am:3003/EPMerchantService.svc/createorder';

  constructor(
    private paymentTransaction: PaymentTransactionService,
    private snackBar: SnackBarUtil,
    private http: HttpClient,
    private paymentTransactionUtil: PaymentTransactionUtil,
  ) {
  }

  initPayment(price: string, product: string, validDays: string, userCard: UserCardResponse, isCommissionActive: boolean, moneyLuxUuid: string | null) {
    const userCardId: string = `${userCard.cardId}-`;
    const paymentTransactionDescription: string = "Transfer money to user";
    const paymentTransactionRequest: PaymentTransactionRequest = this.preparePaymentTransactionRequest(paymentTransactionDescription, userCard, userCardId, price, validDays, moneyLuxUuid);

    this.paymentTransaction.create(paymentTransactionRequest, isCommissionActive).subscribe({
      next: (response: PaymentTransactionResponse) => {
        this.getCredentials().subscribe(easywalletResponse => {
          this.processResponse(easywalletResponse, response, isCommissionActive);
        })

      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 511 && error.error && error.error.message) {
          this.showSnackbar(MockupDataModel.error(DUPLICATE_MONEY_LUX_UUID_MESSAGE), SnackBarUtil.CLASSES.error);
        }
      },
    });
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data,
    });
  }

  private processResponse(easywalletResponse: EasywalletResponse, response: PaymentTransactionResponse, isCommissionActive: boolean) {

    const commission = this.paymentTransactionUtil.getCommission(isCommissionActive, response);
    const rawAmount = response.amount + (response.amount * commission) / 100;
    const amount = String(Math.ceil(rawAmount));
    const issuerId = response.issuerId;
    const key = easywalletResponse.publicKey;
    const decodedId = this.decodeAES(easywalletResponse.id, key);
    const decodedToken = this.decodeAES(easywalletResponse.token, key);
    let checksum = this.generateSecurityCode(decodedId, decodedToken, issuerId, amount);
    const body = {
      MerchantId: decodedId,
      MerchantOrderId: issuerId,
      Amount: amount,
      Checksum: checksum,
    };

    fetch(this.easywalletlUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Content-Length': '<calculated when request is sent>\n',
        'Connection': 'keep-alive',
        'Accept-Encoding': 'gzip, deflate, br\n',
        'Accept': '*/*',
      },
      body: JSON.stringify(body),
    })
      .then(res => {
        if (!res.ok) {
          this.showSnackbar(MockupDataModel.error(PAYMENT_FAILURE_MESSAGE), SnackBarUtil.CLASSES.error);
        }
        return res.json();
      })
      .then(url => {
        window.location.href = url;
      })
      .catch(err => {
        this.showSnackbar(MockupDataModel.error(PAYMENT_FAILURE_MESSAGE), SnackBarUtil.CLASSES.error);
      });
  }

  private preparePaymentTransactionRequest(paymentTransactionDescription: string, userCard: UserCardResponse, userCardId: string, price: string, validDays: string, moneyLuxUuid: string | null) {
    return new PaymentTransactionRequest(
      PaymentMethod.EASY_WALLET,
      paymentTransactionDescription,
      userCard.userId,
      userCardId,
      Number(price),
      Number(validDays),
      userCard.cardId,
      0,
      moneyLuxUuid
    );
  }

  private generateSecurityCode(id: string, token: string, issuer: string, price: string): string {

    return CryptoJS.MD5(id + issuer + price + token).toString();
  }

  initForProductBuying(amount: number, orderId: number) {
    const description: string = 'Product buying';
    const paymentTransactionRequest: PaymentTransactionRequest = new PaymentTransactionRequest(PaymentMethod.EASY_WALLET, description, null, null, amount, 1, null, 0, null, orderId);

    this.paymentTransaction.create(paymentTransactionRequest, false).subscribe({
      next: (response: PaymentTransactionResponse) => {
        this.getCredentials().subscribe(easywalletResponse => {
          this.processResponse(easywalletResponse, response, false);
        })

      }
    });
  }

  getCredentials(): Observable<EasywalletResponse> {
    const url = `${environment.apiUrl}/easywallet/credentials`;
    return this.http.get<EasywalletResponse>(url);
  }

  decodeAES(encryptedBase64: string, key: string): string {
    const keyUtf8 = CryptoJS.enc.Utf8.parse(key);
    const decrypted = CryptoJS.AES.decrypt(encryptedBase64, keyUtf8, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }
}
